import { type InvestmentHolding, useWealthOverview } from '@egw/api'
import { Collapse, GenericError, Link, PageHeader } from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { useRouter } from '@egw/router'
import { Currency, getCurrencySymbol, getFormattedBalanceForOverview } from '@egw/utils'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useFlags } from '@egw/feature-flags'
import { NotFound404 } from '@egw/features-system'
import { buildHoldingsPageData } from '../../../utils'

export const InvestmentsOverviewHoldings: FC = () => {
  const { t } = useTranslation('features-holdings')
  const { slugParams } = useRouter()

  const isApexAccount = true

  const { data: overviewData, isLoading, isError } = useWealthOverview()

  const params = slugParams() as unknown as { assetAllocation: string; category: string }

  const assetAllocationParam = params.assetAllocation

  const categoryParam = params.category

  const data = buildHoldingsPageData({ data: overviewData?.personalPortfolio, assetAllocationParam, categoryParam })

  const pageTitle = data?.category ? t(`category_titles.${data.category}`) : t('holdings.category')

  const pageSubtitle = assetAllocationParam ? t(`holdings.subtitles.${assetAllocationParam}`) : ''

  const chipLabel = t('holdings.percentage_of_asset', { percentage: data?.percentage ?? 0, assetName: pageSubtitle })

  const formattedTotalBalance = data?.amount ? getFormattedBalanceForOverview(data.amount) : undefined

  return isApexAccount ? (
    <NotFound404 />
  ) : (
    <>
      {isError || data === undefined ? (
        <div className="egw-container">
          <GenericError errorMessage={t('holdings.overview_fatal_error')} />
        </div>
      ) : null}
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {!isLoading ? (
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          backLinkUrl={WEALTH_ROUTES.INVESTMENTS_HOLDINGS_OVERVIEW.backPath}
        />
      ) : null}

      <div className="egw-container pt-0">
        {data ? (
          <div data-testid="overview-holdings" className="flex flex-col gap-md">
            <div>
              <span id="transaction_method" className="py-3xs text-text-meta typo-primary-base-light w-fit">
                {chipLabel}
              </span>
              <Collapse
                title={t(`about.titles.${assetAllocationParam}.${categoryParam}`)}
                className="text-action typo-primary-sm-semibold justify-start"
              >
                <div className="p-md bg-alertInfoBg text-text-meta typo-primary-sm-regular flex flex-col gap-md">
                  <p>{t(`about.descriptions.${assetAllocationParam}.${categoryParam}`)}</p>
                </div>
              </Collapse>
            </div>
            <span
              data-testid="overview-customer-networth"
              className="mb-1.5 typo-primary-2xl-light md:typo-primary-3xl-light lg:typo-primary-5xl-light"
              id="total_balance"
            >
              <span
                data-testid="overview-networth-currency"
                className="text-text-meta typo-primary-xl-light relative bottom-0 md:bottom-[.625rem]"
              >
                {getCurrencySymbol(String(data.amount.currencyCode))}
              </span>
              <span
                data-testid="overview-networth-whole"
                className="text-text-header left-3xl text-right typo-primary-3xl-semibold"
              >
                {formattedTotalBalance?.integers}
              </span>
              <span data-testid="overview-networth-decimal" className="text-text-meta typo-primary-2xl-light">
                {formattedTotalBalance?.cents}
              </span>
            </span>
            <div>
              <div className="mt-lg flex flex-col items-start justify-start ">
                {
                  // map over categories
                  data.holdings.map(({ name, symbol, amount }: InvestmentHolding) => {
                    return (
                      <Link
                        href={WEALTH_ROUTES.INVESTMENTS_HOLDINGS_DETAILS_OVERVIEW.path
                          .replace(':assetAllocation', assetAllocationParam)
                          .replace(':category', categoryParam)
                          .replace(':symbol', symbol)}
                        key={symbol}
                        className="flex w-full flex-row justify-between border-light last-of-type:border-b-0 border-b py-md"
                      >
                        <div className="flex flex-row gap-md">
                          {/* name */}
                          <div className="flex flex-col items-start">
                            <p className="text-text-body typo-primary-base-regular">{name}</p>
                            <p className="text-text-meta typo-primary-xs-regular">{symbol}</p>
                          </div>
                        </div>
                        {/* money */}
                        <div className="flex flex-row justify-between ">
                          <div className="flex flex-row gap-3xs">
                            <div className="text-text-header typo-primary-base-semibold">
                              {amount.currencyCode ? getCurrencySymbol(amount.currencyCode) : null}
                            </div>
                            <span className="text-text-header typo-primary-base-semibold">
                              {Currency.integerCents.onlyMinus(amount.amount)}
                            </span>
                          </div>
                        </div>
                      </Link>
                    )
                  })
                }
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}
